/**
 * TalentOS-sourced "Job Overview" tab -- replaces the old
 * Overview/Required Skills/Experience tabs for a job whose analysis_engine
 * is 'talentos'. Foundry-analysed jobs keep the existing tabs untouched;
 * this component is only ever mounted for the TalentOS case (gated in
 * JobDetailPage.tsx by job.analysis_engine + the SHOW_TALENTOS_JOB_UI flag).
 *
 * Most of this comes from GET /talentos/jd-analysis/by-host-job
 * (talentosApi.getJdAnalysisByHostJob). The Benefits/Company Details
 * sidebar cards are the one exception -- those are existing `jobs.*`
 * columns (client/department/location/employment_type/remote_option/
 * benefits), passed in as props since they're not TalentOS-owned and this
 * component has no reason to re-fetch the job itself. Company Details'
 * Website and About the company are the exception to that exception: they
 * come from the TalentOS JD analysis (company_website / company_description,
 * added 2026-10-08), NULL on any analysis run before those columns existed.
 * Per-skill context/rationale (added 2026-10-07) are real TalentOS output,
 * just NULL on any analysis run before that column existed. Anything NULL
 * renders "N/A", never invented.
 *
 * Project Details used to live here, but it had no backing data anywhere in
 * the schema (not jobs.*, not talentos_jd_analyses.*) -- moved to
 * TalentosResumeProfileTab (the candidate side) instead, 2026-10-07, per
 * request. Still placeholder there too until real project data exists.
 *
 * Original Document (2026-10-14): a job created via Upload JD now has
 * `originalFilename` set (jobs.original_filename -- see database.models.Job),
 * and the real uploaded file is fetched/previewed in its native format
 * (PDF inline, DOCX inline via the `docx-preview` library, anything else
 * download-only). A job created via Enter Details or Paste JD, or any job
 * created before this existed, has no original file at all -- that case is
 * unchanged from before: plain-text talentos_jd_analyses.raw_jd_text.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  ListBulletIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { talentosApi } from '@/components/talentos'
import type { TalentosJdSkill } from '@/components/talentos'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { jobsApi } from '@/services/api'

// Mechanical humanization of Skill.category (snake_case in the DB) -- not a
// curated label map, just underscores-to-spaces + capitalized first letter,
// since this is a single-word table cell, not a named section (compare
// TalentosResumeProfileTab's CATEGORY_LABELS, which groups skills under a
// named heading and does need real display names).
const humanizeCategory = (category: string): string =>
  category.length === 0 ? category : category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ')

const REQUIREMENT_LABELS: Record<string, string> = { required: 'Required', preferred: 'Preferred' }
const REQUIREMENT_COLOR: Record<string, string> = { required: 'text-blue-600', preferred: 'text-blue-600' }

const MANDATORY_TYPE_LABELS: Record<string, string> = {
  skill: 'Skill',
  certification: 'Certification',
  domain: 'Domain',
  other: 'Other',
}

const CRITICALITY_PILL: Record<string, string> = {
  core: 'bg-blue-50 text-blue-700 border-blue-200',
  secondary: 'bg-gray-50 text-gray-600 border-gray-200',
}

// value is a ReactNode (not just string) so a field can render a link -- the
// Company Details "Website" field passes an <a>. Empty/null still shows N/A.
const LabeledField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5">{value ? value : <span className="text-gray-300">N/A</span>}</div>
  </div>
)

// Two-up label/value layout -- Company Details and Project Details pair
// fields side by side rather than stacking every field one per row.
const LabeledFieldGrid: React.FC<{ fields: [string, React.ReactNode][] }> = ({ fields }) => (
  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
    {fields.map(([label, value], i) => (
      <LabeledField key={i} label={label} value={value} />
    ))}
  </div>
)

const humanizeFreeText = (value: string | null | undefined): string | null =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ') : null

// Mandatory-requirement rationale strings come from TalentOS already
// prefixed with "Deal-breaker: " (see
// mandatory_requirements_pure.py::build_mandatory_requirements_payload) --
// stripped here for display only, the stored string itself is untouched.
// Also drops the single-quote marks TalentOS wraps the quoted requirement
// phrase in -- same display-only treatment, stored string untouched.
const stripDealBreakerPrefix = (value: string): string => value.replace(/^deal-breaker:\s*/i, '').replace(/'/g, '')

interface Props {
  jobId: string
  jobReadableId: string | null | undefined
  title: string
  client: string | null | undefined
  department: string | null | undefined
  location: string | null | undefined
  employmentType: string | null | undefined
  remoteOption: string | null | undefined
  benefits: string | null | undefined
  // jobs.min_years_experience / max_years_experience -- whatever was in the
  // job-creation form when it was submitted (confirmed 2026-10-05: the one
  // backend path that can set these straight from a Foundry pass with no
  // recruiter review, POST /jobs/analyze-text with create_job=true, has zero
  // live callers in the frontend -- grepped for every caller of
  // jobsApi.analyzeText and every `create_job` reference; the only caller
  // never sends create_job:true, so that branch is dead in practice). Used
  // only as a fallback below when TalentOS's own experience fields are
  // null -- never relabelled as a TalentOS or Foundry finding.
  formMinYearsExperience: number | null | undefined
  formMaxYearsExperience: number | null | undefined
  // jobs.original_filename -- set only for a job created via "Upload JD".
  // Drives "Original Document": present means there's a real uploaded file
  // to preview (native format, via GET /jobs/{id}/download); absent (Enter
  // Details, Paste JD, or any job created before this field existed) keeps
  // the existing plain-text raw_jd_text view unchanged.
  originalFilename: string | null | undefined
}

const TalentosJobProfileTab: React.FC<Props> = ({
  jobId,
  jobReadableId,
  title,
  client,
  department,
  location,
  employmentType,
  remoteOption,
  benefits,
  formMinYearsExperience,
  formMaxYearsExperience,
  originalFilename,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['talentos', 'jd-analysis', 'by-host-job', jobId],
    queryFn: () => talentosApi.getJdAnalysisByHostJob(jobId),
    enabled: !!jobId,
  })

  const [requirementFilter, setRequirementFilter] = useState<string>('all')
  const [expandedSkill, setExpandedSkill] = useState<Record<number, boolean>>({})
  const [docModalOpen, setDocModalOpen] = useState(false)

  // Original Document, file case (originalFilename set — job created via
  // Upload JD): fetch the real file once the modal opens, then preview it in
  // its native format. PDF renders via an <iframe> fed a blob: URL (same
  // visual pattern as RequirementsAgentWidget's PdfPreviewOverlay, blob- not
  // network-URL-sourced since our download endpoint is an authenticated API
  // call, not a public link). DOCX renders via the `docx-preview` library and
  // render pattern already used for the resume-template-formatter's own
  // preview (ResumeFormatterTemplatesPage.tsx) — no new dependency. Anything
  // else (legacy .doc, or an unrecognised extension) falls back to a
  // download-only state; there's no in-repo precedent for previewing those.
  const fileExtension = originalFilename ? originalFilename.toLowerCase().split('.').pop() || '' : ''
  const [fileBlob, setFileBlob] = useState<Blob | null>(null)
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null)
  const docxContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!docModalOpen || !originalFilename) {
      return
    }
    let cancelled = false
    setFileLoading(true)
    setFileError(null)
    setFileBlob(null)
    jobsApi.download(jobId)
      .then(async (response) => {
        if (cancelled) return
        const blob = response.data as Blob
        setFileBlob(blob)
        if (fileExtension === 'docx') {
          const arrayBuffer = await blob.arrayBuffer()
          const container = docxContainerRef.current
          if (container) {
            container.innerHTML = ''
            const docxModule = await import('docx-preview')
            // ignoreWidth/ignoreHeight: without these, docx-preview renders
            // each page at the Word document's own fixed page size (e.g.
            // 8.5in), centered with empty space on either side inside this
            // much wider modal -- making the page fill the container's
            // actual width/content-driven height instead, so it uses the
            // space this modal gives it.
            await docxModule.renderAsync(arrayBuffer, container, undefined, {
              ignoreWidth: true,
              ignoreHeight: true,
            })
            // docx-preview's own default CSS gives its wrapper a gray
            // canvas background and 30px of padding around the page
            // (`.docx-wrapper { background: gray; padding: 30px; }`,
            // mimicking Word's page view) -- both overridden here via inline
            // styles on just this instance's wrapper, rather than a global
            // CSS override, so the admin template-preview feature elsewhere
            // that still wants that gray canvas/page-gap look is unaffected.
            const wrapper = container.querySelector('.docx-wrapper')
            if (wrapper instanceof HTMLElement) {
              wrapper.style.background = '#ffffff'
              wrapper.style.padding = '0'
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) setFileError('Failed to load the original document.')
      })
      .finally(() => {
        if (!cancelled) setFileLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [docModalOpen, originalFilename, jobId, fileExtension])

  useEffect(() => {
    if (!fileBlob || fileExtension !== 'pdf') {
      setPdfObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(fileBlob)
    setPdfObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [fileBlob, fileExtension])

  const downloadOriginalFile = () => {
    if (!originalFilename) return
    const triggerDownload = (blob: Blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = originalFilename
      a.click()
      URL.revokeObjectURL(url)
    }
    if (fileBlob) {
      triggerDownload(fileBlob)
      return
    }
    jobsApi.download(jobId).then((response) => triggerDownload(response.data as Blob))
  }
  const [openSections, setOpenSections] = useState<{
    role: boolean
    skills: boolean
    mand: boolean
    resp: boolean
    benefits: boolean
    company: boolean
  }>({
    role: true,
    skills: true,
    mand: true,
    resp: true,
    benefits: true,
    company: true,
  })

  const skills = data?.skills ?? []

  const requirementCounts = useMemo(
    () =>
      skills.reduce<Record<string, number>>((acc, s) => {
        acc[s.requirement_type] = (acc[s.requirement_type] || 0) + 1
        return acc
      }, {}),
    [skills]
  )

  const filteredSkills = useMemo(
    () => (requirementFilter === 'all' ? skills : skills.filter((s) => s.requirement_type === requirementFilter)),
    [skills, requirementFilter]
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load TalentOS job data.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        TalentOS has not analysed this job yet — it may still be processing.
      </div>
    )
  }

  const mandatoryItems = data.mandatory_requirements?.items ?? []

  // Fallback to the job-creation form's own Min/Max experience fields only
  // when TalentOS itself found nothing in the JD text -- see the
  // formMinYearsExperience/formMaxYearsExperience prop docstring for why
  // this is safe to treat as plain saved form data, not an AI finding.
  const minExperienceYears = data.min_experience_years ?? formMinYearsExperience ?? null
  const maxExperienceYears = data.max_experience_years ?? formMaxYearsExperience ?? null
  const experienceRange =
    data.experience_range ||
    (minExperienceYears != null && maxExperienceYears != null
      ? `${minExperienceYears}–${maxExperienceYears} years`
      : minExperienceYears != null
        ? `${minExperienceYears}+ years`
        : maxExperienceYears != null
          ? `Up to ${maxExperienceYears} years`
          : null)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {/* Role Overview */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, role: !prev.role }))}
            className="flex items-center gap-2 mb-3 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Role Overview</h3>
            {openSections.role ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.role && (
            <div className="rounded-md bg-gray-50 border border-gray-100 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Role Scope</div>
              <p className="text-sm text-gray-700">
                {data.role_scope || <span className="text-gray-300">N/A</span>}
              </p>
            </div>
          )}
        </div>

        {/* Skills */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, skills: !prev.skills }))}
            className="flex items-center gap-2 mb-4 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Skills</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {skills.length}
            </span>
            {openSections.skills ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>

          {openSections.skills && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setRequirementFilter('all')}
                  className={`text-xs font-medium px-3 py-1 rounded-full border ${
                    requirementFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  All {skills.length}
                </button>
                {Object.entries(REQUIREMENT_LABELS).map(([key, label]) =>
                  requirementCounts[key] ? (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setRequirementFilter(key)}
                      className={`text-xs font-medium px-3 py-1 rounded-full border ${
                        requirementFilter === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                      }`}
                    >
                      {label} {requirementCounts[key]}
                    </button>
                  ) : null
                )}
              </div>

              <div className="overflow-x-auto border-t border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-4 pl-1">Skill</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Requirement</th>
                      <th className="py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSkills.map((s: TalentosJdSkill, i) => {
                      const isOpen = !!expandedSkill[i]
                      return (
                        <React.Fragment key={i}>
                          <tr className="border-b border-gray-50 last:border-0">
                            <td className="py-2 pr-4 pl-1 font-medium text-gray-900 whitespace-nowrap">{s.canonical_name}</td>
                            <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{humanizeCategory(s.category)}</td>
                            <td className={`py-2 pr-4 font-semibold whitespace-nowrap ${REQUIREMENT_COLOR[s.requirement_type] || 'text-gray-600'}`}>
                              {REQUIREMENT_LABELS[s.requirement_type] || s.requirement_type}
                            </td>
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setExpandedSkill((prev) => ({ ...prev, [i]: !isOpen }))}
                                className="text-gray-400 hover:text-gray-600"
                                title="Show details"
                              >
                                {isOpen ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                              </button>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="border-b border-gray-50 last:border-0">
                              <td colSpan={4} className="py-2 pl-1 pr-4 bg-gray-50">
                                {/* context (how this skill is used in this role) and
                                    rationale (why this weight) -- added 2026-10-07,
                                    TalentOS was already producing both on every
                                    analysis, core.py just discarded them before this. */}
                                {s.context && <p className="text-xs text-gray-600">{s.context}</p>}
                                {s.rationale && <p className="text-xs text-gray-500 mt-1">{s.rationale}</p>}
                                {!s.context && !s.rationale && <p className="text-xs text-gray-400">N/A</p>}
                                <div className="text-xs text-gray-500 mt-1.5">
                                  {s.minimum_years != null && <span>Minimum years: {s.minimum_years}</span>}
                                  {s.minimum_years != null && s.weight != null && <span className="mx-1.5">·</span>}
                                  {s.weight != null && <span>Match weight: {s.weight.toFixed(2)}</span>}
                                  {s.minimum_years == null && s.weight == null && <span className="text-gray-300">N/A</span>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
                {filteredSkills.length === 0 && <p className="text-sm text-gray-400 py-3">No skills match this filter.</p>}
              </div>
            </>
          )}
        </div>

        {/* Mandatory Requirements */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, mand: !prev.mand }))}
            className="flex items-center gap-2 mb-3 w-full text-left"
            title="Collapse / expand"
          >
            <ClipboardDocumentCheckIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Mandatory Requirements</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {mandatoryItems.length}
            </span>
            {openSections.mand ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.mand &&
            (mandatoryItems.length === 0 ? (
              <p className="text-sm text-gray-300">N/A</p>
            ) : (
              <div className="space-y-2.5">
                {mandatoryItems.map((m, i) => (
                  <div key={i} className="rounded-md border border-gray-100 p-3 flex items-start gap-3">
                    <span className="w-[22px] h-[22px] flex-shrink-0 rounded-full bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">{m.value}</span>
                        <span className="text-xs text-gray-500">{MANDATORY_TYPE_LABELS[m.type] || m.type}</span>
                      </div>
                      {m.rationale && <p className="text-xs text-gray-500 mt-1">{stripDealBreakerPrefix(m.rationale)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>

        {/* Responsibilities */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, resp: !prev.resp }))}
            className="flex items-center gap-2 mb-3 w-full text-left"
            title="Collapse / expand"
          >
            <ListBulletIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Responsibilities</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {data.responsibilities.length}
            </span>
            {openSections.resp ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.resp &&
            (data.responsibilities.length === 0 ? (
              <p className="text-sm text-gray-300">N/A</p>
            ) : (
              <ul className="space-y-2">
                {data.responsibilities.map((r, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                    <span className="text-gray-700 flex-1">{r.responsibility_text}</span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${
                        CRITICALITY_PILL[r.criticality] || 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {r.criticality}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
        </div>

        {/* Benefits -- jobs.benefits, an existing AIRecruit field (not
            TalentOS-owned), shown regardless of engine since it's real data
            we already have. */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, benefits: !prev.benefits }))}
            className="flex items-center gap-2 mb-3 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Benefits</h3>
            {openSections.benefits ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.benefits &&
            (benefits ? (
              <p className="text-sm text-gray-700 whitespace-pre-line">{benefits}</p>
            ) : (
              <p className="text-sm text-gray-300">N/A</p>
            ))}
        </div>

        {/* Company Details -- client/department/location/employment_type/
            remote_option are existing jobs.* columns, wired to real data.
            Website and About the company come from the TalentOS JD analysis
            (talentos_jd_analyses.company_website / company_description),
            extracted from the JD text itself. Website is only saved when the
            URL is written word-for-word in the JD and always starts with
            http(s)://, so it is safe to render as a link. Either one is N/A
            when the JD has no company info, or for jobs analysed before these
            columns existed (re-process the job to fill them). */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, company: !prev.company }))}
            className="flex items-center gap-2 mb-4 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Company Details</h3>
            {openSections.company ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.company && (
            <>
              <LabeledFieldGrid
                fields={[
                  ['Company / Client', client],
                  ['Department', department],
                  ['Location', location],
                  ['Employment type', humanizeFreeText(employmentType)],
                  ['Work mode', humanizeFreeText(remoteOption)],
                  [
                    'Website',
                    data.company_website ? (
                      <a
                        href={data.company_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {data.company_website}
                      </a>
                    ) : null,
                  ],
                ]}
              />
              <div className="mt-3">
                <LabeledField label="About the company" value={data.company_description} />
              </div>
            </>
          )}
        </div>

      </div>

      <div className="space-y-4">
        {/* Role Details */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Role Details</h4>
          <div className="space-y-3">
            <LabeledField label="Role title" value={data.role_title} />
            <LabeledField label="Seniority level" value={humanizeFreeText(data.seniority_level)} />
            <LabeledField label="Domain" value={data.domain} />
            <LabeledField label="Experience range" value={experienceRange} />
            <LabeledField label="Min experience (years)" value={minExperienceYears != null ? String(minExperienceYears) : null} />
            <LabeledField label="Max experience (years)" value={maxExperienceYears != null ? String(maxExperienceYears) : null} />
          </div>
        </div>

        {/* Original Document -- two cases:
            - originalFilename set (job created via Upload JD): the real
              uploaded file, fetched + previewed in its native format.
            - otherwise: talentos_jd_analyses.raw_jd_text (the JD text
              TalentOS actually analysed), as plain text -- unchanged from
              before this file-preview capability existed. Omitted entirely,
              rather than N/A, when there's neither a file nor any text. */}
        {(originalFilename || data.raw_jd_text) && (
          <button
            type="button"
            onClick={() => setDocModalOpen(true)}
            className="w-full bg-white border border-gray-200 rounded-lg p-5 flex items-center gap-3 text-left hover:border-gray-300"
          >
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <DocumentTextIcon className="w-5 h-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-900">Original Document</span>
              <span className="block text-xs text-gray-500">View the job description as uploaded</span>
            </span>
            <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
          </button>
        )}
      </div>

      {docModalOpen && (originalFilename || data.raw_jd_text) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
          onClick={() => setDocModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Original Document"
        >
          <div
            className={`bg-white rounded-2xl shadow-xl w-full flex flex-col ${
              // PDF needs an explicit height (not just max-height): its body
              // is flex-1 so the iframe can fill exactly the space between
              // header/footer, but a flex container with no real height only
              // shrinks to its content's natural size regardless of
              // max-height -- without this the whole modal collapsed down to
              // a sliver (bug fix 2026-10-15). DOCX/text both still want
              // shrink-to-fit-up-to-a-cap (max-height only): docx-preview's
              // content has its own natural height, and plain text should
              // never be forced taller than it needs.
              fileExtension === 'pdf'
                ? 'max-w-5xl h-[92vh] max-h-[92vh]'
                : fileExtension === 'docx'
                  ? 'max-w-5xl max-h-[92vh]'
                  : 'max-w-2xl max-h-[85vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Original Document</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {originalFilename || `${jobReadableId || jobId}_${title.replace(/\s+/g, '_')}.txt`}
                </p>
              </div>
              <button
                onClick={() => setDocModalOpen(false)}
                aria-label="Close"
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            {/* overflow-y-auto dropped for the PDF case specifically -- the
                iframe already has its own internal scrollbar for the
                document, and letting this outer div ALSO scroll produced two
                visible, nested scrollbars once the iframe's fixed height
                didn't exactly match this container's actual available space.
                docx/plain-text still need it: neither has its own internal
                scroll, so this is the only thing that scrolls their
                (potentially taller-than-the-modal) content. */}
            <div className={fileExtension === 'pdf' ? 'px-6 py-5 overflow-hidden flex-1 min-h-0' : 'px-6 py-5 overflow-y-auto'}>
              {originalFilename ? (
                fileExtension === 'docx' ? (
                  // The container must stay mounted regardless of loading
                  // state -- the fetch effect calls docx-preview's
                  // renderAsync() into it via docxContainerRef as soon as the
                  // blob arrives, and a ref to an unmounted element is null.
                  // Conditionally rendering this div only once loading
                  // finished (as every other branch here does) left the ref
                  // null at render time, so renderAsync silently did nothing
                  // -- blank modal, no error (bug fix 2026-10-15).
                  <div className="relative">
                    {fileLoading && (
                      <div className="flex justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    )}
                    {!fileLoading && fileError && (
                      <p className="text-sm text-red-600">{fileError}</p>
                    )}
                    <div ref={docxContainerRef} className="mx-auto bg-white" />
                  </div>
                ) : fileLoading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : fileError ? (
                  <p className="text-sm text-red-600">{fileError}</p>
                ) : fileExtension === 'pdf' ? (
                  pdfObjectUrl && (
                    <iframe
                      // #toolbar=0&navpanes=0 strips Chrome's own built-in
                      // PDF viewer chrome (its dark toolbar, thumbnail
                      // sidebar, zoom/print/page controls) -- without this it
                      // renders as a visually mismatched mini-app bolted
                      // onto this modal, which already has its own
                      // header/Download/Close controls. Just the page
                      // content left, same as the plain-text view it sits
                      // alongside. #view=FitH tells the viewer to zoom so the
                      // page's WIDTH fills the frame -- without it, Chrome's
                      // default "fit whole page" zoom leaves black bars on
                      // either side in a frame this much wider than a
                      // standard page, same gap problem the docx preview had
                      // (fixed by ignoreWidth/ignoreHeight there instead,
                      // since that's a different renderer with no such
                      // option -- this is the PDF-viewer equivalent fix).
                      src={`${pdfObjectUrl}#toolbar=0&navpanes=0&view=FitH`}
                      title="Original Document"
                      className="w-full h-full rounded-lg border border-gray-200"
                    />
                  )
                ) : (
                  <div className="flex flex-col items-center gap-2 py-12 text-center">
                    <DocumentTextIcon className="w-8 h-8 text-gray-300" />
                    <p className="text-sm text-gray-500">
                      {fileExtension === 'doc'
                        ? 'This is a legacy .doc file and cannot be previewed. Please download it to view.'
                        : "This file type can't be previewed inline — download it to view."}
                    </p>
                  </div>
                )
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-line">{data.raw_jd_text}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => {
                  if (originalFilename) {
                    downloadOriginalFile()
                    return
                  }
                  const blob = new Blob([data.raw_jd_text || ''], { type: 'text/plain' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `${jobReadableId || jobId}_${title.replace(/\s+/g, '_')}.txt`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => setDocModalOpen(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TalentosJobProfileTab
